import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, IconButton, Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Save as SaveIcon, Add as AddIcon } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import authInstance from '../../services/httpClient';
import type { ConfirmTagChange } from './TagChangeConfirm';
import { useAdminTheme } from '../../admin-theme';
import { stagingApiBase } from './stagingOrg';

// Name rules (spec section 4 item 6): per Vend type, the first active rule whose
// pattern matches the product name (case-insensitive) adds its tag. Lower priority
// number wins. The server validates (A5) and answers 400 { error: '<field>: …' }.

export interface NameRule {
  id: number;
  vendType: string;
  priority: number;
  pattern: string;
  tag: string;
  active: boolean;
}

interface Draft { vendType: string; priority: string; pattern: string; tag: string }

// Evaluated per call: the org comes from the route, and this module is shared by both pages.
const base = () => `${stagingApiBase()}/staging/name-rules`;
const EMPTY: Draft = { vendType: '', priority: '', pattern: '', tag: '' };

/** The server's 400 message, else a generic one. */
export function errorMessage(err: any, fallback: string): string {
  return err?.response?.data?.error || err?.message || fallback;
}

/** Send priority as a JSON number; '' or junk becomes NaN, which the server rejects by name. */
const toPriority = (s: string): number => (s.trim() === '' ? NaN : Number(s));

function RuleRow({ rule, onSaved, onDeleted, confirm }: {
  rule: NameRule;
  onSaved: (r: NameRule, res?: unknown) => void;
  onDeleted: (id: number, res?: unknown) => void;
  confirm?: ConfirmTagChange;
}) {
  const { colors } = useAdminTheme();
  const [draft, setDraft] = useState<Draft>({ ...rule, priority: String(rule.priority) });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = draft.priority !== String(rule.priority) || draft.pattern !== rule.pattern || draft.tag !== rule.tag;

  const send = async (body: Partial<NameRule>) => {
    setBusy(true);
    setError(null);
    try {
      const next = { vendType: rule.vendType, priority: rule.priority, pattern: rule.pattern, tag: rule.tag, active: rule.active, ...body };
      if (confirm && !(await confirm({ kind: 'name_rule', id: rule.id, rule: next }))) return;
      const { data } = await authInstance.put(`${base()}/${rule.id}`, body);
      const saved: NameRule = data?.rule ?? { ...rule, ...body };
      onSaved(saved, data);
      setDraft({ ...saved, priority: String(saved.priority) });
    } catch (err) {
      setError(errorMessage(err, 'Could not save the rule'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      if (confirm && !(await confirm({ kind: 'name_rule', id: rule.id, rule: null }))) { setBusy(false); return; }
      const { data } = await authInstance.delete(`${base()}/${rule.id}`);
      onDeleted(rule.id, data);
    } catch (err) {
      setError(errorMessage(err, 'Could not delete the rule'));
      setBusy(false);
    }
  };

  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const cell = { borderBottom: `1px solid ${colors.border}`, verticalAlign: 'top' };

  return (
    <TableRow>
      <TableCell sx={{ ...cell, width: 90 }}>
        <TextField size="small" type="number" value={draft.priority} onChange={set('priority')}
          inputProps={{ 'aria-label': `Priority for rule ${rule.id}`, min: 1 }} />
      </TableCell>
      <TableCell sx={cell}>
        <TextField size="small" fullWidth value={draft.pattern} onChange={set('pattern')}
          inputProps={{ 'aria-label': `Pattern for rule ${rule.id}`, style: { fontFamily: 'monospace' } }} />
        {error && <Typography variant="caption" role="alert" sx={{ color: colors.error, display: 'block', mt: 0.5 }}>{error}</Typography>}
      </TableCell>
      <TableCell sx={cell}>
        <TextField size="small" fullWidth value={draft.tag} onChange={set('tag')}
          inputProps={{ 'aria-label': `Tag for rule ${rule.id}` }} />
      </TableCell>
      <TableCell sx={{ ...cell, width: 70 }}>
        <Switch size="small" checked={rule.active} disabled={busy} onChange={(e) => send({ active: e.target.checked })}
          inputProps={{ 'aria-label': `Active rule ${rule.id}` }} />
      </TableCell>
      <TableCell sx={{ ...cell, width: 90, whiteSpace: 'nowrap' }}>
        <Tooltip title="Save">
          <span>
            <IconButton size="small" aria-label={`Save rule ${rule.id}`} disabled={!dirty || busy}
              onClick={() => send({ priority: toPriority(draft.priority), pattern: draft.pattern, tag: draft.tag })}>
              <SaveIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <span>
            <IconButton size="small" aria-label={`Delete rule ${rule.id}`} disabled={busy} onClick={remove}
              sx={{ '&:hover': { color: colors.error } }}>
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function AddRuleRow({ onAdded, confirm }: { onAdded: (r: NameRule, res?: unknown) => void; confirm?: ConfirmTagChange }) {
  const { colors } = useAdminTheme();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { vendType: draft.vendType.trim(), priority: toPriority(draft.priority), pattern: draft.pattern, tag: draft.tag.trim() };
      if (confirm && !(await confirm({ kind: 'name_rule', rule: { ...body, active: true } }))) return;
      const { data } = await authInstance.post(base(), body);
      onAdded(data.rule, data);
      setDraft(EMPTY);
    } catch (err) {
      setError(errorMessage(err, 'Could not add the rule'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="Vend type" value={draft.vendType} onChange={set('vendType')}
          inputProps={{ 'aria-label': 'New rule Vend type' }} />
        <TextField size="small" label="Priority" type="number" value={draft.priority} onChange={set('priority')}
          sx={{ width: 100 }} inputProps={{ 'aria-label': 'New rule priority', min: 1 }} />
        <TextField size="small" label="Pattern" value={draft.pattern} onChange={set('pattern')}
          sx={{ flex: 1, minWidth: 160 }} inputProps={{ 'aria-label': 'New rule pattern', style: { fontFamily: 'monospace' } }} />
        <TextField size="small" label="Tag" value={draft.tag} onChange={set('tag')}
          inputProps={{ 'aria-label': 'New rule tag' }} />
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={add}
          disabled={busy || !draft.vendType.trim() || !draft.pattern || !draft.tag.trim()}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          Add rule
        </Button>
      </Box>
      {error && <Typography variant="caption" role="alert" sx={{ color: colors.error, display: 'block', mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
}

export type NameRuleMutation = 'added' | 'saved' | 'deleted';

/** onMutated gets the server response (refreshed / withdrawn / refreshError) and what happened, after each save, add or delete. */
export default function NameRulesEditor({ onMutated, confirm }: { onMutated?: (res: any, what: NameRuleMutation) => void; confirm?: ConfirmTagChange } = {}) {
  const { colors } = useAdminTheme();
  const notify = useNotify();
  const [rules, setRules] = useState<NameRule[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await authInstance.get(base());
      setRules(data.rules || []);
    } catch (err) {
      notify(errorMessage(err, 'Could not load name rules'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const byType = new Map<string, NameRule[]>();
    for (const r of [...rules].sort((a, b) => a.vendType.localeCompare(b.vendType) || a.priority - b.priority)) {
      byType.set(r.vendType, [...(byType.get(r.vendType) ?? []), r]);
    }
    return Array.from(byType.entries());
  }, [rules]);

  const saved = (r: NameRule, res?: unknown) => { setRules((rs) => rs.map((x) => (x.id === r.id ? r : x))); onMutated?.(res, 'saved'); };
  const deleted = (id: number, res?: unknown) => { setRules((rs) => rs.filter((x) => x.id !== id)); onMutated?.(res, 'deleted'); };
  const added = (r: NameRule, res?: unknown) => { setRules((rs) => [...rs, r]); onMutated?.(res, 'added'); };
  const head = { color: colors.textMuted, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' as const, borderBottom: `1px solid ${colors.border}` };

  return (
    <Paper sx={{ bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.textPrimary }}>Name rules</Typography>
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          Per Vend type, the first active rule (lowest priority number) whose pattern matches the product name adds its tag.
          Patterns are case-insensitive regular expressions, re-applied on every sync.
        </Typography>
      </Box>
      {!loading && groups.length === 0 && (
        <Typography variant="body2" sx={{ px: 2, pb: 2, color: colors.textMuted }}>No name rules yet.</Typography>
      )}
      {groups.map(([vendType, list]) => (
        <Box key={vendType} sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: colors.textPrimary, mb: 0.5 }}>{vendType}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Priority</TableCell>
                <TableCell sx={head}>Pattern</TableCell>
                <TableCell sx={head}>Tag</TableCell>
                <TableCell sx={head}>Active</TableCell>
                <TableCell sx={head} />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((r) => <RuleRow key={r.id} rule={r} onSaved={saved} onDeleted={deleted} confirm={confirm} />)}
            </TableBody>
          </Table>
        </Box>
      ))}
      <AddRuleRow onAdded={added} confirm={confirm} />
    </Paper>
  );
}
